import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from 'store';

import { AuthUserType } from 'common/graphql/types';
import { CILogger } from 'common/log/log.provider';

interface SalesforceConfig {
    orgId: string;
    deploymentName: string;
    deploymentUrl: string;
    scrt2Url: string;
}

// Matches the prod-host check used for other environment-specific URLs, e.g. src/module/patient/shared/print/utils.ts
const isProductionHost = (): boolean => window.location.hostname === 'lxportal.clinicalink.com';

// Evaluated lazily (not at module load) so window.location.hostname is read at the time
// the chat widget is actually injected, rather than being frozen at import time.
const getSalesforceConfig = (): SalesforceConfig =>
    isProductionHost()
        ? {
              orgId: '00DF00000005eZw',
              deploymentName: 'LX_Portal_Chat',
              deploymentUrl: 'https://clinicalink.my.site.com/ESWLXPortalChat1779734988346',
              scrt2Url: 'https://clinicalink.my.salesforce-scrt.com',
          }
        : {
              orgId: '00DOt00000xf70L',
              deploymentName: 'Clinical_Ink_qa',
              deploymentUrl: 'https://clinicalink--partial.sandbox.my.site.com/ESWClinicalInkqa1781631755869',
              scrt2Url: 'https://clinicalink--partial.sandbox.my.salesforce-scrt.com',
          };

// Stable reference required so removeEventListener matches the registered listener.
// SF dispatches onEmbeddedMessagingWindowMinimized before it re-shows the launcher button,
// so hideChatButton() must be deferred to the next paint frame via requestAnimationFrame.
const hideSFNativeButton = () => {
    requestAnimationFrame(() => {
        window.embeddedservice_bootstrap?.utilAPI?.hideChatButton?.();
    });
};

/**
 * Returns true when the current user is eligible to see the Salesforce chat widget
 * (authenticated, non-patient). Shared with mock/placeholder components so the
 * show/hide logic lives in exactly one place.
 */
export const useSalesforceChatEnabled = (): boolean => {
    const activeUser = useSelector((state: RootState) => state.auth.activeUser);
    return !!activeUser && activeUser.userType !== AuthUserType.PATIENT;
};

/**
 * Injects the Salesforce Embedded Messaging chat widget for authenticated site users.
 * Handles CILogger error visibility on CDN failures and DOM cleanup on logout.
 */
export const useSalesforceChat = () => {
    const chatEnabled = useSalesforceChatEnabled();
    const scriptsInjected = useRef(false);

    useEffect(() => {
        if (!chatEnabled) {
            document.getElementById('sf-embedded-messaging-init')?.remove();
            document.getElementById('sf-embedded-messaging-bootstrap')?.remove();
            document.getElementById('embedded-messaging')?.remove();
            document.getElementById('sf-hide-launcher-style')?.remove();
            if (window.embeddedservice_bootstrap) {
                window.embeddedservice_bootstrap.onEmbeddedMessagingButtonCreated = undefined;
            }
            window.removeEventListener('onEmbeddedMessagingWindowMinimized', hideSFNativeButton);
            scriptsInjected.current = false;
            return;
        }
        if (scriptsInjected.current) {
            return;
        }

        scriptsInjected.current = true;

        const sfConfig = getSalesforceConfig();

        // Hide SF's native launcher in our DOM. We render our own SupportChatTab and drive the chat
        // via launchChat(), so SF's launcher should never show. A CSS rule is timing-independent —
        // unlike hideChatButton()/event listeners, it cannot lose the race when SF re-shows a button.
        //
        // The minimized "Expand the chat window" button lives INSIDE the cross-origin SF iframe
        // (#embeddedMessagingFrame), so we cannot target that button directly from our document.
        // However, the <iframe> element itself lives in our document, and SF adds the `isMinimized`
        // class to it when the chat is minimized. Hiding the iframe in that state hides the button
        // with it. Clicking our SupportChatTab calls launchChat(), which removes `isMinimized` and
        // restores the full chat window (our rule no longer matches). We also hide the closed-state
        // launcher button, which renders in our document.
        const hideLauncherStyle = document.createElement('style');
        hideLauncherStyle.id = 'sf-hide-launcher-style';
        hideLauncherStyle.textContent =
            '#embeddedMessagingConversationButton, .embeddedMessagingConversationButton, #embeddedMessagingFrame.isMinimized, .embeddedMessagingFrame.isMinimized { display: none !important; }';
        document.head.appendChild(hideLauncherStyle);

        // Script 1: define initEmbeddedMessaging as a named global — SF-provided code block.
        // The onEmbeddedMessagingButtonCreated callback is set here (before init()) so it is in
        // place when SF first creates the button, and fires again each time SF re-renders the
        // button (e.g. after the user minimizes the chat window). This matches the pattern from
        // the official SF docs and community: https://salesforce.stackexchange.com/a/438452
        const initScript = document.createElement('script');
        initScript.id = 'sf-embedded-messaging-init';
        initScript.type = 'text/javascript';
        initScript.textContent = `
            function initEmbeddedMessaging() {
                try {
                    embeddedservice_bootstrap.settings.language = 'en_US'; // For example, enter 'en' or 'en-US'
                    embeddedservice_bootstrap.settings.hideChatButtonOnLoad = true;
                    embeddedservice_bootstrap.onEmbeddedMessagingButtonCreated = function() {
                        if (embeddedservice_bootstrap.utilAPI) {
                            embeddedservice_bootstrap.utilAPI.hideChatButton();
                        }
                    };
                    embeddedservice_bootstrap.init(
                        '${sfConfig.orgId}',
                        '${sfConfig.deploymentName}',
                        '${sfConfig.deploymentUrl}',
                        {
                            scrt2URL: '${sfConfig.scrt2Url}',
                        }
                    );
                } catch (err) {
                    console.error('Error loading Embedded Messaging: ', err);
                }
            }
        `;
        document.body.appendChild(initScript);

        // Script 2: SF CDN bootstrap. The onload handler calls initEmbeddedMessaging() after the
        // bootstrap has fully executed and created its internal DOM nodes — matching the canonical
        // SF snippet: <script onload="initEmbeddedMessaging()" src="bootstrap.min.js">.
        // This fixes: TypeError: Failed to execute 'observe' on 'MutationObserver': parameter 1 is not of type 'Node'
        const bootstrapScript = document.createElement('script');
        bootstrapScript.id = 'sf-embedded-messaging-bootstrap';
        bootstrapScript.type = 'text/javascript';
        bootstrapScript.src = `${sfConfig.deploymentUrl}/assets/js/bootstrap.min.js`;
        bootstrapScript.onload = () => {
            window.initEmbeddedMessaging?.();
        };
        bootstrapScript.onerror = (e) => {
            CILogger.getInstance().error('Salesforce chat CDN failed to load', e);
            scriptsInjected.current = false;
        };
        document.body.appendChild(bootstrapScript);

        window.addEventListener('onEmbeddedMessagingWindowMinimized', hideSFNativeButton);

        return () => {
            if (window.embeddedservice_bootstrap) {
                window.embeddedservice_bootstrap.onEmbeddedMessagingButtonCreated = undefined;
            }
            window.removeEventListener('onEmbeddedMessagingWindowMinimized', hideSFNativeButton);
        };
    }, [chatEnabled]);
};
